import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { useAuth } from '../contexts/AuthContext';
import UsernameField from './UsernameField';
import Header from './Header';
import Footer from './Footer';
import ProfileNav from './ProfileNav';
import { getTierLabel, getTierColor, getTierIcon, hasDeepDiverAccess } from '../utils/tiers';
import { apiFetch, getApiHost } from '../utils/api';
import { trackEvent } from '../utils/analytics';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading, linkWallet, linkEmail, refreshUser } = useAuth();
  const { connected, publicKey } = useWallet();
  const { setVisible } = useWalletModal();

  const [linkEmailInput, setLinkEmailInput] = useState('');
  const [isLinkingEmail, setIsLinkingEmail] = useState(false);
  const [isLinkingWallet, setIsLinkingWallet] = useState(false);
  const [showLinkEmailForm, setShowLinkEmailForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const handleCancelSubscription = async () => {
    if (!window.confirm('Cancel your subscription?\n\nYou\'ll keep access until the end of your current billing period; after that you revert to whatever DNET-only tier you qualify for.')) return;
    setCanceling(true);
    setError(null);
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to cancel subscription');
      }
      const data = await response.json();
      setSuccess(data.message || 'Subscription scheduled to cancel.');
      await refreshUser();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription');
    } finally {
      setCanceling(false);
    }
  };

  const handleManageSubscription = async () => {
    setManagingSubscription(true);
    setError(null);
    try {
      const token = localStorage.getItem('deepnets_token');
      const response = await apiFetch(`${getApiHost()}/api/stripe/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create portal session');
      }
      const data = await response.json();
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open subscription portal');
    } finally {
      setManagingSubscription(false);
    }
  };

  // Redirect to home if not authenticated
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isLoading, isAuthenticated, navigate]);

  // Stripe subscription checkout returns here with ?subscription=success|canceled
  // (configured in stripe.ts success_url / cancel_url). Fire the SPA-visible
  // funnel event so PostHog can measure abandonment on Stripe's hosted page,
  // refresh the user so the new tier shows immediately on success, and strip
  // the param so a refresh doesn't re-fire.
  const [searchParams, setSearchParams] = useSearchParams();
  const subscriptionStatus = searchParams.get('subscription');
  useEffect(() => {
    if (subscriptionStatus === 'success' || subscriptionStatus === 'canceled') {
      trackEvent(
        subscriptionStatus === 'success'
          ? 'stripe_checkout_completed'
          : 'stripe_checkout_canceled',
        { source: 'profile_page' },
      );
      if (subscriptionStatus === 'success') refreshUser();
      const t = setTimeout(() => {
        searchParams.delete('subscription');
        setSearchParams(searchParams, { replace: true });
      }, 5000);
      return () => clearTimeout(t);
    }
  }, [subscriptionStatus, refreshUser, searchParams, setSearchParams]);

  const handleLinkEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkEmailInput.trim()) return;

    setIsLinkingEmail(true);
    setError(null);
    try {
      await linkEmail(linkEmailInput.trim());
      setShowLinkEmailForm(false);
      setLinkEmailInput('');
      setSuccess('Email linked successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link email');
    } finally {
      setIsLinkingEmail(false);
    }
  };

  const handleLinkWallet = async () => {
    if (!connected) {
      setVisible(true);
      return;
    }

    setIsLinkingWallet(true);
    setError(null);
    try {
      await linkWallet();
      setSuccess('Wallet linked successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to link wallet');
    } finally {
      setIsLinkingWallet(false);
    }
  };

  // Auto-link wallet when it connects
  useEffect(() => {
    if (connected && publicKey && isLinkingWallet) {
      handleLinkWallet();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, publicKey]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setSuccess('Copied to clipboard!');
    setTimeout(() => setSuccess(null), 2000);
  };

  const truncateAddress = (address: string): string => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-crypto-dark">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-8 h-8 border-2 border-crypto-accent border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-crypto-dark">
      <Header />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-2xl font-bold text-white mb-6">Account</h1>
        <ProfileNav />

        {/* Notifications */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}
        {success && (
          <div className="mb-4 p-3 bg-crypto-accent/10 border border-crypto-accent/30 rounded-lg">
            <p className="text-crypto-accent text-sm">{success}</p>
          </div>
        )}

        {/* Account info */}
        <div className="bg-crypto-gray border border-gray-700 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">Account Information</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {/* Username */}
            <UsernameField
              currentUsername={user.username}
              onSaved={() => refreshUser()}
            />

            {/* Wallet address */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Wallet</label>
              {user.walletAddress ? (
                <div className="flex items-center gap-2">
                  <code className="text-white text-sm font-mono">{truncateAddress(user.walletAddress)}</code>
                  <button
                    onClick={() => copyToClipboard(user.walletAddress!)}
                    className="text-gray-400 hover:text-crypto-accent transition-colors"
                    title="Copy full address"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLinkWallet}
                  disabled={isLinkingWallet}
                  className="text-crypto-accent hover:text-crypto-accent/80 text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {isLinkingWallet ? 'Linking...' : '+ Link Wallet'}
                </button>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              {user.email ? (
                <span className="text-white text-sm">{user.email}</span>
              ) : showLinkEmailForm ? (
                <form onSubmit={handleLinkEmail} className="flex gap-2">
                  <input
                    type="email"
                    value={linkEmailInput}
                    onChange={(e) => setLinkEmailInput(e.target.value)}
                    className="flex-1 bg-crypto-dark border border-gray-700 rounded-lg px-3 py-1.5 text-white placeholder-gray-500 focus:outline-none focus:border-crypto-accent transition-colors text-sm"
                    placeholder="Enter email"
                    required
                  />
                  <button
                    type="submit"
                    disabled={isLinkingEmail}
                    className="px-3 py-1.5 bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark font-medium rounded-lg transition-colors disabled:opacity-50 text-sm"
                  >
                    {isLinkingEmail ? '...' : 'Link'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowLinkEmailForm(false)}
                    className="px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-sm"
                  >
                    ✕
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setShowLinkEmailForm(true)}
                  className="text-crypto-accent hover:text-crypto-accent/80 text-sm font-medium transition-colors"
                >
                  + Link Email
                </button>
              )}
            </div>

            {/* Member since */}
            <div>
              <label className="block text-sm text-gray-400 mb-1">Member Since</label>
              <span className="text-white text-sm">{formatDate(user.createdAt)}</span>
            </div>
          </div>
        </div>

        {/* Tier card.
              - At top tier with subscription → Manage (Stripe portal)
              - At top tier without subscription → ghosted Upgrade
              - Below top tier → Upgrade → /tiers
              When a subscription is active, also surface its renewal
              date and a low-priority Cancel link. */}
        {(() => {
          const currentTier = user.tier || 'free';
          const atTop = hasDeepDiverAccess(currentTier);
          const sub = user.subscription;
          const hasSubscription = !!sub;
          const isCanceling = !!sub && (sub.cancelAtPeriodEnd === true || !!sub.cancelAt);
          const cancelDateRaw = sub?.cancelAt || sub?.currentPeriodEnd || null;
          const renewalDateLabel = cancelDateRaw ? new Date(cancelDateRaw).toLocaleDateString() : null;
          return (
            <div className="bg-crypto-gray border border-gray-700 rounded-xl p-6">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getTierIcon(currentTier) || '🌐'}</span>
                  <span className={`text-xl font-bold ${getTierColor(currentTier)}`}>
                    {getTierLabel(currentTier)}
                  </span>
                </div>
                {atTop ? (
                  hasSubscription ? (
                    <button
                      onClick={handleManageSubscription}
                      disabled={managingSubscription}
                      className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      {managingSubscription ? 'Loading…' : 'Manage'}
                    </button>
                  ) : (
                    <button
                      disabled
                      className="bg-gray-800 text-gray-500 px-4 py-2 rounded-lg text-sm font-medium cursor-not-allowed"
                      title="You're at the top tier"
                    >
                      Upgrade →
                    </button>
                  )
                ) : (
                  <Link
                    to="/tiers"
                    className="bg-crypto-accent hover:bg-crypto-accent/80 text-crypto-dark px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Upgrade →
                  </Link>
                )}
              </div>
              {hasSubscription && sub && renewalDateLabel && (
                <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-500">
                  <span>
                    via {getTierLabel(sub.tier)} subscription ·{' '}
                    {isCanceling
                      ? <span className="text-amber-400">canceling {renewalDateLabel}</span>
                      : <>renews {renewalDateLabel}</>}
                  </span>
                  {!isCanceling && (
                    <button
                      onClick={handleCancelSubscription}
                      disabled={canceling}
                      className="text-gray-500 hover:text-gray-300 underline-offset-2 hover:underline transition-colors disabled:opacity-50"
                    >
                      {canceling ? 'Canceling…' : 'Cancel subscription'}
                    </button>
                  )}
                </div>
              )}
              {user.cryptoSubscription?.active && (
                <div className="mt-3 flex items-center justify-between gap-3 text-xs text-gray-500">
                  <span>
                    via {getTierLabel(user.cryptoSubscription.tier)} USDC subscription ·{' '}
                    expires {new Date(user.cryptoSubscription.currentPeriodEnd).toLocaleDateString()}
                  </span>
                  <Link to="/tiers" className="text-gray-500 hover:text-gray-300 underline-offset-2 hover:underline transition-colors">
                    Extend
                  </Link>
                </div>
              )}
            </div>
          );
        })()}
      </main>

      <Footer />
    </div>
  );
};

export default ProfilePage;
